import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { downloadUrl } from "../lib/api";
import { fmtTL, fmtDate, fmtCreator } from "../lib/format";
import { Lock, FileDown, Trash2, History, ChevronDown, ChevronRight } from "lucide-react";
import { FilterTh } from "../components/ColFilters";
import { groupByDay, dayKey } from "./entries/entryGroups";
import { ENTRIES_LIMIT } from "./dataentry/useEntryData";
import EntryDayPanel from "./entries/EntryDayPanel";
export { EditLogRow } from "./entries/EditLogRow";

// sütun filtreleri kayıt bazında çalışır (useColFilters); liste gün gruplarını gösterir
export const ENTRY_COLS = [
  { key: "entry_date", label: "Tarih", type: "datemulti", fmt: fmtDate },
  { key: "project_name", label: "Proje" },
  { key: "location_name", label: "Lokasyon" },
  { key: "service_name", label: "Hizmet" },
  { key: "subcontractor_name", label: "Taşeron", value: (r) => r.subcontractor_name || "—" },
  { key: "_doer", label: "İşi Yapan Taşeron", value: (r) => r.actual_subcontractor_name || r.subcontractor_name || "—" },
  { key: "quantity", label: "Miktar", type: "number" },
  { key: "customer_total", label: "Müşteri", type: "number" },
  { key: "subcontractor_total", label: "Taşeron Fiyatı", type: "number" },
  { key: "gross_profit", label: "Brüt Kâr", type: "number" },
  { key: "status", label: "Durum", options: [{ value: "Aktif", label: "Aktif" }, { value: "İptal", label: "İptal" }] },
  { key: "_creator", label: "Oluşturan", value: (r) => r.created_by || "—" },
];
const GROUP_KEYS = ["entry_date", "project_name", "location_name", "subcontractor_name", "_doer", "customer_total", "subcontractor_total", "gross_profit", "_creator"];
const SUB_COL_KEYS = ["subcontractor_name", "_doer", "subcontractor_total", "gross_profit"];

function GroupCheck({ g, apprSel, setApprSel }) {
  const ref = useRef(null);
  const ids = g.main.filter((e) => !e.is_correction).map((e) => e.id);   // düzeltme farkları onaya gönderilmez
  const n = ids.filter((id) => apprSel.includes(id)).length;
  useEffect(() => { if (ref.current) ref.current.indeterminate = n > 0 && n < ids.length; }, [n, ids.length]);
  return <input ref={ref} type="checkbox" className="mr-1.5 align-middle" checked={ids.length > 0 && n === ids.length} disabled={!ids.length} onClick={(ev) => ev.stopPropagation()}
    onChange={() => setApprSel(n === ids.length ? apprSel.filter((id) => !ids.includes(id)) : [...new Set([...apprSel, ...ids])])} title="Günün kayıtlarını seç" data-testid={`entry-appr-check-${g.key}`} />;
}

// Veri girişi listesi: her satır bir gün (tarih + proje + lokasyon); satıra tıklayınca hemen altında günün hizmetleri ve Düzelt / İptal / Sil / Kapat açılır (ESC kapatır, kapanınca kaldırma konumu geri gelir)
export default function EntriesTable({ visEntries, entries, ehf, setEhf, eAutoOpts, isAdmin, lockOf, openEdit, onEditDay, reactivate, remove, cancel, cancelMany, reactivateMany, userLabels, apprSel = [], setApprSel, onBulkDelete, hideSub = false, title = "Tüm Kayıtlar", subtitle }) {
  const [openKey, setOpenKey] = useState(null);
  const scrollRef = useRef(null);
  const posRef = useRef(null);
  const cols = ENTRY_COLS.filter((c) => GROUP_KEYS.includes(c.key) && !(hideSub && SUB_COL_KEYS.includes(c.key)));
  const isRight = (c) => c.type === "number";
  const groups = useMemo(() => groupByDay(visEntries, entries, lockOf), [visEntries, entries, lockOf]);
  const selectable = visEntries.filter((e) => !e.is_price_diff && !e.is_correction).map((e) => e.id);
  const allSelected = selectable.length > 0 && selectable.every((id) => apprSel.includes(id));
  const toggleAll = () => setApprSel(allSelected ? apprSel.filter((id) => !selectable.includes(id)) : [...new Set([...apprSel, ...selectable])]);
  const toggle = (key) => {
    if (openKey === key) return setOpenKey(null);
    if (!posRef.current) posRef.current = { top: scrollRef.current?.scrollTop ?? 0, y: window.scrollY };
    setOpenKey(key);
  };
  const close = () => setOpenKey(null);
  const { search } = useLocation();
  useEffect(() => {   // ?kayit=<id> (ör. hakedişten "Kayda Git"): kaydın günü otomatik açılır, satır vurgulanır
    const id = new URLSearchParams(search).get("kayit");
    if (!id) return;
    const e = entries.find((x) => x.id === id);
    if (e) setOpenKey(dayKey(e));
  }, [search, entries]);
  useLayoutEffect(() => {   // kapanınca açılmadan önceki kaydırma konumuna dön
    if (openKey || !posRef.current) return;
    const p = posRef.current; posRef.current = null;
    if (scrollRef.current) scrollRef.current.scrollTop = p.top;
    window.scrollTo({ top: p.y });
  }, [openKey]);
  useEffect(() => {   // açılınca liste tam görünsün: sığıyorsa alt kenarı, sığmıyorsa gün satırı üste kaydırılır
    if (!openKey) return;
    const t = setTimeout(() => {
      const row = document.querySelector(`[data-testid="entry-day-row-${openKey}"]`);
      const panel = document.querySelector(`[data-testid="entry-day-panel-${openKey}"]`);
      if (!row || !panel) return;
      const top = row.getBoundingClientRect().top, bottom = panel.getBoundingClientRect().bottom;
      if (bottom <= window.innerHeight && top >= 0) return;
      const fits = bottom - top <= window.innerHeight - 24;
      (fits ? panel : row).scrollIntoView({ block: fits ? "end" : "start", behavior: "smooth" });
    }, 60);
    return () => clearTimeout(t);
  }, [openKey]);
  useEffect(() => {
    if (!openKey) return;
    const onKey = (ev) => {
      if (ev.key !== "Escape" || document.querySelector('[data-testid="entry-edit-banner"]') || ev.target.closest("[data-searchable]")) return;   // düzeltme açıksa ESC önce düzeltmeyi kapatır
      ev.preventDefault(); ev.stopImmediatePropagation(); close();   // bölüm kapanmasın, yalnız liste kapansın
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [openKey]);
  const cell = (g, c) => {
    switch (c.key) {
      case "entry_date": return <span className="whitespace-nowrap">{fmtDate(g.entry_date)} <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-bold" data-testid={`entry-day-count-${g.key}`}>{g.main.length} hizmet</span>{g.cancelled.length > 0 && <span className="ml-1 text-[10px] font-semibold text-slate-600">{g.cancelled.length} iptal</span>}{g.lock && <Lock className="w-3 h-3 inline ml-1 text-slate-500" title={`Hakediş: ${g.lock.invoice_no}`} />}{g.hasEdits && <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 text-[9px] font-bold" title="Günde düzeltilmiş kayıt var"><History className="w-3 h-3" /> DÜZELTİLDİ</span>}</span>;
      case "project_name": return g.project_name;
      case "location_name": return g.location_name;
      case "subcontractor_name": return g.subs.join(" · ") || "—";
      case "_doer": { const diff = g.doers.some((d) => !g.subs.includes(d)); return <span className={diff ? "font-semibold text-sky-700" : ""} title={diff ? "İşi yapan taşeron atanandan farklı" : undefined}>{g.doers.join(" · ") || "—"}</span>; }
      case "customer_total": return fmtTL(g.cust);
      case "subcontractor_total": return fmtTL(g.sub);
      case "gross_profit": return <span className="font-semibold text-emerald-700">{fmtTL(g.profit)}{g.ff > 0 && <span className="block text-[9px] font-normal text-red-700">fark düşüldü</span>}</span>;
      case "_creator": return g.creators.length <= 1 ? fmtCreator(g.creators[0], g.createdAt, userLabels) : <span title={g.creators.join(", ")}>{g.creators.length} kullanıcı</span>;
      default: return null;
    }
  };
  return (
  <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="entries-table">
    <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-sm font-semibold text-slate-900" data-testid="entries-table-title">{title} <span className="text-xs font-normal text-slate-500">({entries.length} kayıt · {groups.length} gün)</span></div>
        <div className="text-xs text-slate-500">{subtitle || "Aktif + iptal edilen kayıtlar"} · satıra tıklayın: günün hizmetleri ve Düzelt / İptal / Sil hemen altında açılır (ESC kapatır){setApprSel ? " · kutucukla çoklu seçim" : ""}</div>
        {entries.length >= ENTRIES_LIMIT && <div className="mt-1 text-xs font-semibold text-amber-800" data-testid="entries-limit-notice">Son {ENTRIES_LIMIT} kayıt listeleniyor — daha eski kayıtlar bu listede ve filtrelerde yer almaz.</div>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {setApprSel && apprSel.length > 0 && (
          <>
            <span className="text-xs font-semibold text-slate-700" data-testid="entries-selected-count">{apprSel.length} kayıt seçili</span>
            <button type="button" onClick={() => setApprSel([])} data-testid="entries-clear-selection" className="px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-xs text-slate-600 hover:bg-slate-50">Seçimi Temizle</button>
            {isAdmin && onBulkDelete && (
              <button type="button" onClick={() => onBulkDelete(apprSel)} data-testid="entries-bulk-delete" title="Yalnız yönetici — seçili kayıtları kalıcı olarak siler"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-700 text-white text-xs font-semibold hover:bg-red-800">
                <Trash2 className="w-3.5 h-3.5" /> Seçilenleri Sil ({apprSel.length})
              </button>
            )}
          </>
        )}
        <a href={downloadUrl("/entries/export")} target="_blank" rel="noreferrer" className="excel-btn shrink-0" data-testid="entries-export-excel">
          <FileDown className="w-4 h-4" /> Excel İndir
        </a>
      </div>
    </div>
    <div ref={scrollRef} className="scroll-xy max-h-[26rem]" data-testid="entries-scroll">
      <table className="w-full tbl">
        <thead className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_#e2e8f0]">
          <tr>
            {cols.map((c, i) => (
              <th key={c.key} className={`px-3 py-2.5 text-xs uppercase tracking-wider text-slate-600 font-semibold ${isRight(c) ? "text-right" : "text-left"}`}>
                {i === 0 && setApprSel && <input type="checkbox" className="mr-1.5 align-middle" checked={allSelected} disabled={!selectable.length} onChange={toggleAll} title={allSelected ? "Görünen seçimi kaldır" : "Görünen tüm kayıtları seç"} data-testid="entry-select-all" />}
                <FilterTh col={c} hf={ehf} setHf={setEhf} testid={`entry-hf-${c.key}`} align={isRight(c) ? "right" : "left"} autoOptions={eAutoOpts[c.key]} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const open = openKey === g.key;
            return (
            <Fragment key={g.key}>
            <tr onClick={() => toggle(g.key)} className={`border-t border-slate-100 cursor-pointer ${open ? "row-active" : ""} ${!g.active.length && g.main.length ? "opacity-60" : ""}`} data-testid={`entry-day-row-${g.key}`} data-open={open ? "1" : "0"} title={open ? "Kapat" : "Günün hizmetlerini aç"}>
              {cols.map((c, i) => (
                <td key={c.key} className={`px-3 py-2 text-xs ${isRight(c) ? "numeric-cell" : ""} ${i === 0 ? "whitespace-nowrap" : ""}`}>
                  {i === 0 && <>{open ? <ChevronDown className="w-3.5 h-3.5 inline mr-1 text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 inline mr-1 text-slate-400" />}{setApprSel && <GroupCheck g={g} apprSel={apprSel} setApprSel={setApprSel} />}</>}
                  {cell(g, c)}
                </td>
              ))}
            </tr>
            {open && <tr data-testid={`entry-day-expand-${g.key}`}><td colSpan={cols.length} className="p-0">
              <EntryDayPanel g={g} entries={entries} hideSub={hideSub} isAdmin={isAdmin} lockOf={lockOf} openEdit={openEdit} onEditDay={onEditDay} cancel={cancel} reactivate={reactivate} remove={remove} cancelMany={cancelMany} reactivateMany={reactivateMany} onBulkDelete={onBulkDelete} userLabels={userLabels} onClose={close} />
            </td></tr>}
            </Fragment>
            );
          })}
          {entries.length === 0 && (
            <tr><td colSpan={cols.length} className="text-center py-8 text-sm text-slate-500">Kayıt yok</td></tr>
          )}
          {entries.length > 0 && visEntries.length === 0 && (
            <tr><td colSpan={cols.length} className="text-center py-8 text-sm text-slate-500" data-testid="entries-filter-empty">Filtrelerle eşleşen kayıt yok</td></tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
  );
}
